// soccer_admin/src/services/api.js
import axios from 'axios';

const API = axios.create({
  baseURL: 'https://api.soccerup.games/api',
  withCredentials: true,
  timeout: 15000,
});

API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_authenticated');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Admin login
export const adminLogin = (username, password) => 
  API.post('/admin/login', { username, password });
export const adminLogout = () => API.post('/admin/logout');

// User management with pagination
export const fetchAdminUsers = (page = 1, limit = 20) => 
  API.get(`/admin/users?page=${page}&limit=${limit}`);

// Card management with pagination
export const fetchAdminCards = (page = 1, limit = 20) => 
  API.get(`/admin/cards?page=${page}&limit=${limit}`);

// Matches with pagination and filters
export const fetchAdminMatches = (page = 1, limit = 20, filters = {}) => {
  let url = `/admin/matches?page=${page}&limit=${limit}`;
  if (filters.competition) url += `&competition=${filters.competition}`;
  if (filters.from) url += `&from=${filters.from}`;
  if (filters.to) url += `&to=${filters.to}`;
  return API.get(url);
};

// Support messages
export const fetchSupportMessages = () => API.get('/support/admin/all');
export const sendAdminReply = (messageId, reply) => 
  API.post('/support/admin/reply', { messageId, reply });

// Season reset
export const resetDomesticSeason = (continent) => 
  API.post(`/admin/reset-domestic-season/${continent}`);
export const resetContinentalSeason = (competition) => 
  API.post(`/admin/reset-continental-season/${competition}`);

// User cards for a specific user
export const fetchUserCards = (userId) => API.get(`/admin/user-cards/${userId}`);

// Notification
export const adminSendNotification = (data) => API.post('/notifications/admin/send', data);
export const adminDeleteNotification = (id) => API.delete(`/notifications/admin/${id}`);

// Qualified users
export const getQualifiedUsers = (competition, continent = null) => {
  let url = `/admin/qualified-users/${competition}`;
  if (competition === 'domestic' && continent) {
    url += `?continent=${continent}`;
  }
  return API.get(url);
};

export const deleteUser = (userId) => API.delete(`/admin/users/${userId}`);

// Sponsorship
export const fetchSponsorshipRequests = () => API.get('/sponsorship/admin/requests');
export const updateSponsorshipStatus = (id, status) => 
  API.put(`/sponsorship/admin/requests/${id}`, { status });

// Premium names
export const fetchPremiumPurchases = () => API.get('/premium-names/admin/purchases');
export const approvePremiumPurchase = (purchaseId) => 
  API.put(`/premium-names/admin/purchases/${purchaseId}/approve`);
export const rejectPremiumPurchase = (purchaseId, reason) => 
  API.put(`/premium-names/admin/purchases/${purchaseId}/reject`, { reason });
export const addPremiumName = (name, tier, price) => 
  API.post('/premium-names/admin/add', { name, tier, price });
export const deletePremiumName = (id) => 
  API.delete(`/premium-names/admin/${id}`);
export const fetchPremiumNameStats = () => 
  API.get('/premium-names/admin/stats');

// Dashboard stats (unchanged)
export const fetchUserContinentStats = () => API.get('/admin/stats/user-continents');
export const fetchCompetitionDistribution = () => API.get('/admin/stats/competition-distribution');
export const fetchTopScorers = () => API.get('/admin/stats/top-scorers');
export const fetchTopPlayers = () => API.get('/admin/stats/top-players');
export const fetchRecentMatches = () => API.get('/admin/stats/recent-matches');
export const fetchRecentUsers = () => API.get('/admin/stats/recent-users');
export const fetchSeasonOverview = () => API.get('/admin/stats/season-overview');
export const fetchRevenueStats = () => API.get('/admin/stats/revenue');
export const fetchActivityStats = () => API.get('/admin/stats/activity');

// ===== Error Logging =====
export const fetchErrorLogs = (page = 1, limit = 30, filters = {}) => {
  let url = `/admin/errors?page=${page}&limit=${limit}`;
  if (filters.severity) url += `&severity=${filters.severity}`;
  if (filters.resolved !== undefined && filters.resolved !== '') url += `&resolved=${filters.resolved}`;
  return API.get(url);
};

// ✅ تحسين دالة resolveError لتتعامل مع الاستجابة بشكل أفضل
export const resolveError = async (errorId) => {
  try {
    const response = await API.put(`/admin/errors/${errorId}/resolve`);
    // إرجاع البيانات الكاملة من الخادم
    return response.data;
  } catch (error) {
    console.error('Error resolving error:', error);
    // إعادة رمي الخطأ ليتم التعامل معه في المتصل
    throw error;
  }
};

export default API;